import React, { useEffect, Suspense, lazy } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Sidebar from "./components/Sidebar";
import ErrorBoundary from "./components/ErrorBoundary";
import ProtectedRoute from "./components/ProtectedRoute";
import { Toaster } from "@/components/ui/toaster";
import { supabaseAuth } from "./lib/supabaseClient";
import { initHubAuth } from "./lib/hubAuth";


// Capture hub token from URL BEFORE any components render
initHubAuth();

// Clear any leftover dark theme
localStorage.removeItem('theme');
document.documentElement.classList.remove('dark');

import useKeyboardShortcuts from "./hooks/useKeyboardShortcuts";
import ShortcutsDialog from "./components/ShortcutsDialog";

// Lazy-loaded pages for code splitting
const Home = lazy(() => import("./pages/Home"));
const Templates = lazy(() => import("./pages/Templates"));
const Catalog = lazy(() => import("./pages/Catalog"));
const QuoteBuilder = lazy(() => import("./pages/QuoteBuilder"));
const StoreFront = lazy(() => import("./pages/StoreFront"));
const Checkout = lazy(() => import("./pages/Checkout"));
const Contacts = lazy(() => import("./pages/Contacts"));
const Reports = lazy(() => import("./pages/Reports"));
const Settings = lazy(() => import("./pages/Settings"));
const RequestQuote = lazy(() => import("./pages/RequestQuote"));
const Orders = lazy(() => import("./pages/Orders"));
const Login = lazy(() => import("./pages/Login"));
const AuthCallback = lazy(() => import("./pages/AuthCallback"));
const LetsTalk = lazy(() => import("./pages/LetsTalk"));
const MyOrders = lazy(() => import("./pages/MyOrders"));
const InstantQuote = lazy(() => import("./pages/InstantQuote"));
const OrderConfirmation = lazy(() => import("./pages/OrderConfirmation"));
const NotFound = lazy(() => import("./pages/NotFound"));
const AllContacts = lazy(() => import("./pages/AllContacts"));
const ContentLibrary = lazy(() => import("./pages/ContentLibrary"));
const Documents = lazy(() => import("./pages/Documents"));
const Forms = lazy(() => import("./pages/Forms"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const AcceptInvite = lazy(() => import("./pages/AcceptInvite"));

const PageLoader = () => (
  <div className="flex items-center justify-center h-screen w-full">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
  </div>
);

function AppLayout() {
  const location = useLocation();
  const hideSidebar = location.pathname === '/login' || location.pathname === '/auth/callback' || location.pathname === '/lets-talk' || location.pathname === '/onboarding' || location.pathname.startsWith('/invite/');

  // Global keyboard shortcuts
  useKeyboardShortcuts();

  return (
    <div className={`flex min-h-screen bg-gray-50 dark:bg-gray-950`}>
      {!hideSidebar && <Sidebar />}
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/lets-talk" element={<LetsTalk />} />
        <Route path="/onboarding" element={<Onboarding />} />
        <Route path="/invite/accept" element={<AcceptInvite />} />

        {/* Customer-facing routes (auth handled by customer portal) */}
        <Route path="/" element={<StoreFront />} />
        <Route path="/store" element={<StoreFront />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/request-quote" element={<RequestQuote />} />
        <Route path="/my-orders" element={<ProtectedRoute><MyOrders /></ProtectedRoute>} />
        <Route path="/instant-quote" element={<InstantQuote />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />

        {/* Protected admin routes */}
        <Route path="/home" element={<ProtectedRoute><Home /></ProtectedRoute>} />
        <Route path="/templates" element={<ProtectedRoute><Templates /></ProtectedRoute>} />
        <Route path="/catalog" element={<ProtectedRoute><Catalog /></ProtectedRoute>} />
        <Route path="/quote-builder" element={<ProtectedRoute><QuoteBuilder /></ProtectedRoute>} />
        <Route path="/contacts" element={<ProtectedRoute><Contacts /></ProtectedRoute>} />
        <Route path="/reports" element={<ProtectedRoute><Reports /></ProtectedRoute>} />
        <Route path="/orders" element={<ProtectedRoute><Orders /></ProtectedRoute>} />
        <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
        <Route path="/all-contacts" element={<ProtectedRoute><AllContacts /></ProtectedRoute>} />
        <Route path="/content-library" element={<ProtectedRoute><ContentLibrary /></ProtectedRoute>} />
        <Route path="/documents" element={<ProtectedRoute><Documents /></ProtectedRoute>} />
        <Route path="/forms" element={<ProtectedRoute><Forms /></ProtectedRoute>} />

        {/* Catch-all 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
      <Toaster />
      <ShortcutsDialog />
    </div>
  );
}

function App() {
  // Refresh auth session periodically (every 4 minutes)
  useEffect(() => {
    const interval = setInterval(async () => {
      const { data: { session } } = await supabaseAuth.auth.getSession();
      if (session) {
        const expiresAt = session.expires_at;
        const now = Math.floor(Date.now() / 1000);
        if (expiresAt && expiresAt - now < 300) {
          await supabaseAuth.auth.refreshSession();
        }
      }
    }, 4 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  return (
    <ErrorBoundary>
      <BrowserRouter>
        <AppLayout />
      </BrowserRouter>
    </ErrorBoundary>
  );
}

export default App;
